import type { OrderDetailsPath } from '@src/containers/Trading/Markets/OrderDetails/types';
import type { DealBookingType } from '@src/types/OTC';
import type {
  Column,
  CustomerQuote,
  LedgerAccountTypeEnum,
  MarketDataCardConfig,
  PredictionMarketCardConfig,
} from '@talos/kyoko';
import type { MultilegComboType } from '../components/MultilegCombo/enums';
import type { OptionFilter, StrikeFilter } from '../containers/Trading/Markets/Options/Filter/types';

export interface CurrentMarketTabDefaults {
  defaultSubAccountId: number | undefined;
}

export const MarketTabType = {
  Market: 'Market',
  PredictionMarket: 'PredictionMarket',
  DeepDive: 'DeepDive',
  OrderDetails: 'OrderDetails',
  Option: 'Option',
  Multileg: 'Multileg',
  DealProductBuilder: 'DealProductBuilder',
  ReconDetails: 'ReconDetails',
  AccountLedgerEventsDetails: 'AccountLedgerEventsDetails',
  PositionAutoHedging: 'PAH Rules',
  CareOrders: 'Care Orders',
  ManualRFQs: 'Manual RFQs',
} as const;
export type MarketTabType = (typeof MarketTabType)[keyof typeof MarketTabType];

export interface IMarketTab {
  type: MarketTabType;
  id: string;
  label: string;
  isTemporary?: boolean;
  // temporary tabs can now be save-able or not
  canSave?: boolean;
}

/** @deprecated Use MarketCardPanelConfig instead */
export interface GeneralMarketTab extends IMarketTab {
  type: typeof MarketTabType.Market;
  columns?: MarketDataCardConfig[][];
  defaults?: Partial<CurrentMarketTabDefaults>;
}

/** @deprecated Use PredictionMarketPanelConfig instead */
export interface PredictionMarketTab extends IMarketTab {
  type: typeof MarketTabType.PredictionMarket;
  columns?: PredictionMarketCardConfig[][];
}

/** @deprecated Use DeepDivePanelConfig instead */
export type DeepDiveMarketTabData = {
  selectedMarkets: string[];
  selectedMarketAccounts: string[];
  selectedAggregation?: string;
  priceIncrement?: string;
  showMarkets?: boolean;
  showCumQty?: boolean;
  chartResolution?: string;
};

/** @deprecated Use DeepDivePanelConfig instead */
export interface DeepDiveMarketTab extends IMarketTab {
  type: typeof MarketTabType.DeepDive;
  symbol: string;
  data: DeepDiveMarketTabData;
}

/** @deprecated Use PositionAutoHedgingRulesPanelConfig instead */
export interface PositionAutoHedgingTab extends IMarketTab {
  type: typeof MarketTabType.PositionAutoHedging;
}

/** @deprecated Use ReconMismatchesDetailsPanelConfig instead */
export interface ReconDetailsMarketTab extends IMarketTab {
  type: typeof MarketTabType.ReconDetails;
  mismatchIDs: string[];
}

/** @deprecated Use AccountLedgerEventsDetailsPanelConfig instead */
export interface AccountLedgerEventsDetailsMarketTab extends IMarketTab {
  type: typeof MarketTabType.AccountLedgerEventsDetails;
  account: string;
  accountType: LedgerAccountTypeEnum;
  asset?: string;
}

/** @deprecated Use OrderDetailsPanelConfig instead */
export interface OrderDetailsMarketTab extends IMarketTab {
  type: typeof MarketTabType.OrderDetails;
  orderID: string;
  openTab: OrderDetailsPath;
}

export interface CareOrdersTabData {
  // TODO fhqvst We're not entirely sure what will live here yet, so leaving empty
}

/** @deprecated Use CareOrdersPanelConfig instead */
export interface CareOrdersTab extends IMarketTab {
  type: typeof MarketTabType.CareOrders;
  data: CareOrdersTabData;
}

export interface ManualRFQsTabData {
  defaultColumns?: (keyof CustomerQuote | Partial<Column>)[];
  showAllDecimals?: boolean;
}

/** @deprecated Use ManualRFQsPanelConfig instead */
export interface ManualRFQsTab extends IMarketTab {
  type: typeof MarketTabType.ManualRFQs;
  data: ManualRFQsTabData;
}

export type OptionMarketTabData = {
  optionFilter: OptionFilter;
  strikeFilter?: StrikeFilter;
  columnDefsVisibility?: Record<string, boolean>;
};

/** @deprecated Use OptionsPanelConfig instead */
export interface OptionMarketTab extends IMarketTab {
  type: typeof MarketTabType.Option;
  data?: OptionMarketTabData;
}

export type MultilegTabData = {
  instrumentType: MultilegComboType;
  resolvedSymbol?: string;
  legSymbols?: string[];
  legMarketAccounts?: string[];
  legMarkets?: string[];
  initiatingLegs?: boolean[];
  haircutRatios?: string[];
  compactOrderBook?: boolean;
};

/** @deprecated Use MultilegPanelConfig instead */
export interface MultilegMarketTab extends IMarketTab {
  type: typeof MarketTabType.Multileg;
  data: MultilegTabData;
}

export type DealProductBuilderTabData = {
  bookingType: DealBookingType;
  resolvedSymbol?: string;
};

/** @deprecated Use DealProductBuilderPanelConfig instead */
export interface DealProductBuilderMarketTab extends IMarketTab {
  type: typeof MarketTabType.DealProductBuilder;
  data: DealProductBuilderTabData;
}

/** @deprecated Use PanelConfig instead */
export type MarketTab =
  | GeneralMarketTab
  | PredictionMarketTab
  | DeepDiveMarketTab
  | OrderDetailsMarketTab
  | OptionMarketTab
  | MultilegMarketTab
  | DealProductBuilderMarketTab
  | ReconDetailsMarketTab
  | AccountLedgerEventsDetailsMarketTab
  | PositionAutoHedgingTab
  | CareOrdersTab
  | ManualRFQsTab;
